import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';
import { Layout, Menu, Button, AutoComplete, Input, Drawer } from 'antd';
import { SearchOutlined, MessageOutlined, ShoppingOutlined, CommentOutlined, UserOutlined, MenuOutlined } from '@ant-design/icons';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import './header.css';
import LoginModal from './auth/LoginModal';

const { Header } = Layout;

const HeaderComponent = () => {
  const [user] = useAuthState(auth);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [menuVisible, setMenuVisible] = useState(false);

  const handleMenuItemClick = () => {
    setMenuVisible(false);
  };

  const menuItems = [
    {
      key: 'buysell',
      icon: <ShoppingOutlined />,
      label: <Link to="/buysell" onClick={handleMenuItemClick}>
        <span className={location.pathname === '/buysell' ? 'active-nav-item' : 'menu-item-text'}>Buy & Sell</span>
      </Link>,
    },
    {
      key: 'forums',
      icon: <CommentOutlined />,
      label: <Link to="/forums" onClick={handleMenuItemClick}>
        <span className={location.pathname === '/forums' ? 'active-nav-item' : 'menu-item-text'}>Forums</span>
      </Link>,
    },
    {
      key: 'messages',
      icon: <MessageOutlined />,
      label: user ? (
        <Link to="/messages" onClick={handleMenuItemClick}>
          <span className={location.pathname === '/messages' ? 'active-nav-item' : 'menu-item-text'}>Messages</span>
        </Link>
      ) : (
        <span onClick={() => {
          setMenuVisible(false);
          setShowLoginModal(true);
        }}>
          <span className="menu-item-text">Messages</span>
        </span>
      ),
    },
  ];

  useEffect(() => {
    const fetchItems = async () => {
      const itemsCollection = collection(db, 'ads');
      const itemsSnapshot = await getDocs(itemsCollection);
      const itemsList = itemsSnapshot.docs.map(doc => ({
        label: doc.data().ItemName,
        id: doc.id
      }));
      setSearchOptions(itemsList);
    };

    fetchItems();
  }, []);

  const handleSignOut = () => {
    auth.signOut();
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    const filtered = searchOptions.filter(option =>
      option.label.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredOptions(filtered);
  };

  const onSelect = (value) => {
    window.location.href = `/buysell?search=${encodeURIComponent(value)}`;
    setSearchTerm('');
  };

  const renderSearchOptions = () => {
    const options = [{
      label: 'Items',
      options: filteredOptions.map(option => ({
        value: option.label,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {option.label}
            <SearchOutlined />
          </div>
        ),
      })),
    }];
    return options;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuVisible && !event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-trigger')) {
        setMenuVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuVisible]);

  return (
    <>
      <Header 
        className="header-main"
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Link 
          to="/" 
          className="logo" 
          onClick={() => setMenuVisible(false)}
        >
          <h1 className="brand-text">Totly Store</h1>
        </Link>
        
        <div className="mobile-menu-trigger" onClick={() => setMenuVisible(!menuVisible)}>
          <MenuOutlined />
        </div>

        <Menu
          mode="horizontal"
          items={menuItems}
          style={{
            background: 'transparent',
            flex: '0 0 auto',
          }}
          className="nav-menu"
        />

        <div className="desktop-search" style={{ flex: '1 1 auto', maxWidth: '300px', margin: '0 24px' }}>
          <AutoComplete
            popupClassName="search-dropdown"
            style={{ width: '100%' }}
            options={renderSearchOptions()}
            onSelect={onSelect}
            onSearch={handleSearch}
            value={searchTerm}
            onChange={setSearchTerm}
          >
            <Input.Search 
              size="large" 
              placeholder="Search items"
              onSearch={(value) => {
                if (value) {
                  window.location.href = `/buysell?search=${encodeURIComponent(value)}`;
                }
              }}
            />
          </AutoComplete>
        </div>

        <div className="header-buttons" style={{ flex: '0 0 auto' }}>
          {user ? (
            <>
              <Button 
                type="link" 
                icon={<UserOutlined />}
                onClick={() => window.location.href = '/profile'}
                className="header-btn"
              >
                My Profile
              </Button>
              <Button 
                type="link" 
                onClick={handleSignOut}
                className="header-btn"
              >
                Log Out
              </Button>
            </>
          ) : (
            <Button 
              type="link"
              onClick={() => setShowLoginModal(true)}
              className="header-btn"
            >
              Log In/Sign Up
            </Button>
          )}
        </div>
      </Header>

      <div className={`mobile-menu ${menuVisible ? 'open' : ''}`}>
        <div className="mobile-menu-search">
          <AutoComplete
            popupClassName="search-dropdown"
            style={{ width: '100%', margin: '12px 0' }}
            options={renderSearchOptions()}
            onSelect={(value) => {
              onSelect(value);
              setMenuVisible(false);
            }}
            onSearch={handleSearch}
            value={searchTerm}
            onChange={setSearchTerm}
          >
            <Input.Search 
              size="large" 
              placeholder="Search items"
              onSearch={(value) => {
                if (value) {
                  window.location.href = `/buysell?search=${encodeURIComponent(value)}`;
                  setMenuVisible(false);
                }
              }}
            />
          </AutoComplete>
        </div>
        
        <Menu
          mode="vertical"
          items={[
            ...menuItems,
            ...(user ? [
              {
                key: 'profile',
                icon: <UserOutlined />,
                label: <Link to="/profile" onClick={handleMenuItemClick}>
                  <span className={location.pathname === '/profile' ? 'active-nav-item' : 'menu-item-text'}>My Profile</span>
                </Link>,
              },
              {
                key: 'logout',
                label: <span onClick={() => {
                  handleSignOut();
                  setMenuVisible(false);
                }}>
                  <span className="menu-item-text">Log Out</span>
                </span>,
              }
            ] : [
              {
                key: 'login',
                label: <span onClick={() => {
                  setShowLoginModal(true);
                  setMenuVisible(false);
                }}>
                  <span className="menu-item-text">Log In/Sign Up</span>
                </span>,
              }
            ])
          ]}
          style={{ background: 'transparent' }}
          className="mobile-menu-items"
        />
      </div>

      <LoginModal 
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={() => setShowLoginModal(false)}
      />
    </>
  );
};

export default HeaderComponent;